import React, { useState, useEffect } from 'react';
import { api, adminAPI } from '../services/api';
import { dateTimeToSGTIso, formatDateSGT, todaySGT } from '../utils/dateUtils';
import '../assets/DisputeResolutionModal.css';

const DisputeResolutionModal = ({ 
  isOpen, 
  onClose, 
  tutor, 
  month, 
  year, 
  onDisputeResolved 
}) => {
  const [disputedLessons, setDisputedLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingLesson, setEditingLesson] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [savedEdits, setSavedEdits] = useState({}); // Store saved edits per lesson
  const [resolutionModal, setResolutionModal] = useState({ isOpen: false, lesson: null });
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && tutor && month && year) {
      fetchDisputedLessons();
    }
  }, [isOpen, tutor, month, year]);

  const fetchDisputedLessons = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('[DisputeResolutionModal] Fetching disputed lessons for:', {
        tutorId: tutor._id,
        month,
        year
      });
      
      // Fetch the tutor's monthly summary with disputed lessons
      const response = await api.get(`/endorsements/tutor/${tutor._id}/${month}/${year}`);
      
      console.log('[DisputeResolutionModal] API response:', response.data);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        console.log('[DisputeResolutionModal] Data structure:', {
          hasLessons: !!data.lessons,
          lessonsCount: data.lessons?.length,
          hasSummary: !!data.summary,
          disputedLessonsInSummary: data.summary?.disputedLessons
        });
        
        // Get lessons from the top level (not from summary)
        const lessons = data.lessons || [];
        console.log('[DisputeResolutionModal] Total lessons:', lessons.length);
        
        // Filter only disputed lessons
        const disputed = lessons.filter(lesson => lesson.isDisputed);
        console.log('[DisputeResolutionModal] Disputed lessons found:', disputed.length);
        console.log('[DisputeResolutionModal] Disputed lesson IDs:', disputed.map(l => l._id));
        
        setDisputedLessons(disputed);
      } else {
        console.log('[DisputeResolutionModal] No data found in response');
        setDisputedLessons([]);
      }
    } catch (err) {
      setError('Failed to load disputed lessons');
      console.error('[DisputeResolutionModal] Error fetching disputed lessons:', err);
      console.error('[DisputeResolutionModal] Error response:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = (lessonId) => {
    // Validate edited data
    if (!editedData.date || !editedData.duration || !editedData.tutorRate) {
      alert('Please fill in all required fields (Date, Duration, Tutor Rate)');
      return;
    }

    if (editedData.duration <= 0) {
      alert('Duration must be greater than 0');
      return;
    }

    if (editedData.tutorRate < 0) {
      alert('Tutor Rate must be a positive number');
      return;
    }

    // Store the edited data for this lesson (convert duration back to minutes for backend)
    setSavedEdits(prev => ({
      ...prev,
      [lessonId]: { 
        ...editedData,
        duration: editedData.duration * 60 // Convert hours to minutes
      }
    }));
    setEditingLesson(null);
    setEditedData({});
    alert('Changes saved. Click "Resolve Dispute" to apply changes and resolve the dispute.');
  };

  const handleResolveDispute = async () => {
    if (!resolutionModal.lesson) return;

    try {
      setSubmitting(true);
      
      console.log('[DisputeResolution] Starting dispute resolution for lesson:', resolutionModal.lesson._id);
      
      // Check if this lesson has saved edits
      const lessonEdits = savedEdits[resolutionModal.lesson._id];
      console.log('[DisputeResolution] Saved edits:', lessonEdits);
      
      const updatedLessonData = lessonEdits ? {
        // Interpret the picked date/time as Singapore time (SGT) and send a
        // UTC ISO string to the backend. Using dateTimeToSGTIso avoids the
        // local-vs-UTC off-by-one where a non-SGT browser shifted the day.
        date: lessonEdits.date
          ? dateTimeToSGTIso(lessonEdits.date, lessonEdits.time || '12:00')
          : lessonEdits.date,
        duration: lessonEdits.duration, // Already in minutes from handleSaveEdit
        mode: lessonEdits.mode,
        tutorRate: lessonEdits.tutorRate,
        summary: lessonEdits.summary
      } : null;

      console.log('[DisputeResolution] Updated lesson data:', updatedLessonData);
      console.log('[DisputeResolution] Resolution notes:', resolutionNotes);

      const response = await api.put(
        `/endorsements/resolve-dispute/${resolutionModal.lesson._id}`,
        {
          resolution: resolutionNotes || 'Dispute resolved by administrator',
          updatedLessonData
        }
      );

      console.log('[DisputeResolution] Response:', response.data);

      if (response.data.success) {
        alert('Dispute resolved successfully');
        setResolutionModal({ isOpen: false, lesson: null });
        setResolutionNotes('');
        setEditedData({});
        setEditingLesson(null);
        // Clear saved edits for this lesson
        setSavedEdits(prev => {
          const newEdits = { ...prev };
          delete newEdits[resolutionModal.lesson._id];
          return newEdits;
        });
        // Refresh the disputed lessons list
        await fetchDisputedLessons();
        // Notify parent component that dispute was resolved
        if (onDisputeResolved) {
          onDisputeResolved();
        }
      }
    } catch (err) {
      console.error('[DisputeResolution] Error resolving dispute:', err);
      console.error('[DisputeResolution] Error response:', err.response?.data);
      console.error('[DisputeResolution] Error message:', err.message);
      
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to resolve dispute';
      alert(`Failed to resolve dispute: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    const lesson = disputedLessons.find(l => l._id === lessonId);
    if (!lesson) return;

    const confirmMessage = `⚠️ PERMANENT DELETE\n\nAre you sure you want to permanently delete this lesson?\n\nTutee: ${lesson.tutee?.name}\nSubject: ${lesson.subject}\nDate: ${formatDate(lesson.date)}\n\nThis action CANNOT be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await adminAPI.delete(`/lessons/${lessonId}`);

      if (response.data.success) {
        alert('Lesson deleted successfully');
        // Clear any saved edits for this lesson
        setSavedEdits(prev => {
          const newEdits = { ...prev };
          delete newEdits[lessonId];
          return newEdits;
        });
        // Refresh the disputed lessons list
        await fetchDisputedLessons();
        // Notify parent component
        if (onDisputeResolved) {
          onDisputeResolved();
        }
      }
    } catch (err) {
      console.error('Error deleting lesson:', err);
      alert(err.response?.data?.message || 'Failed to delete lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Pin to Asia/Singapore so the day never shifts on a non-SGT browser.
    return formatDateSGT(dateString);
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || 'Unknown';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !submitting && onClose()}>
      <div className="modal-content dispute-resolution-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h3>Resolve Disputes - {tutor?.name}</h3>
            <span className="modal-subtitle">
              {getMonthName(month)} {year}
            </span>
            {!loading && disputedLessons.length > 0 && (
              <span className="dispute-count-badge">{disputedLessons.length} dispute{disputedLessons.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <button 
            className="modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <p>Loading disputed lessons...</p>
            </div>
          ) : disputedLessons.length === 0 ? (
            <div className="no-disputes no-disputes-success">
              <div className="no-disputes-icon">✓</div>
              <h4 className="no-disputes-title">No Disputed Lessons</h4>
              <p className="no-disputes-message">
                All lessons for {tutor?.name} in {getMonthName(month)} {year} have been reviewed.<br/>
                There are currently no disputes to resolve.
              </p>
            </div>
          ) : (
            <div className="disputed-lessons">
              {disputedLessons.map(lesson => (
                <div key={lesson._id} className="dispute-card">
                  <div className="dispute-card-header">
                    <div className="dispute-badges">
                      <span className="dispute-badge">⚠️ Disputed</span>
                      {savedEdits[lesson._id] && (
                        <span className="edited-badge">✏️ Edited</span>
                      )}
                    </div>
                    <span className="dispute-date">
                      Disputed on {formatDate(lesson.disputedAt)} at {new Date(lesson.disputedAt).toLocaleTimeString('en-SG', { 
                        timeZone: 'Asia/Singapore',
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </span>
                  </div>

                  <div className="lesson-details">
                    <div className="detail-row">
                      <span className="detail-label">Tutee:</span>
                      <span className="detail-value">{lesson.tutee?.name || 'Unknown'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Subject:</span>
                      <span className="detail-value">{lesson.subject}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Date:</span>
                      {editingLesson === lesson._id ? (
                        <input
                          type="date"
                          className="edit-input"
                          value={editedData.date}
                          onChange={(e) => setEditedData({ ...editedData, date: e.target.value })}
                        />
                      ) : (
                        <span className="detail-value">
                          {formatDate(savedEdits[lesson._id]?.date || lesson.date)}
                        </span>
                      )}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Time:</span>
                      {editingLesson === lesson._id ? (
                        <input
                          type="time"
                          className="edit-input"
                          value={editedData.time || ''}
                          onChange={(e) => setEditedData({ ...editedData, time: e.target.value })}
                        />
                      ) : (
                        <span className="detail-value">
                          {savedEdits[lesson._id]?.time ? 
                            // savedEdits time is already an SGT HH:MM string from
                            // the edit-load; render it as-is (adding a timeZone to
                            // a locally-parsed 2000-01-01 value would shift it).
                            savedEdits[lesson._id].time :
                            lesson.date ? new Date(lesson.date).toLocaleTimeString('en-SG', { 
                              timeZone: 'Asia/Singapore',
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true 
                            }) : 'N/A'
                          }
                        </span>
                      )}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Duration:</span>
                      {editingLesson === lesson._id ? (
                        <input
                          type="number"
                          className="edit-input"
                          value={editedData.duration}
                          onChange={(e) => setEditedData({ ...editedData, duration: parseFloat(e.target.value) })}
                          min="0.25"
                          step="0.25"
                        />
                      ) : (
                        <span className="detail-value">
                          {savedEdits[lesson._id]?.duration !== undefined ? 
                            (savedEdits[lesson._id].duration / 60).toFixed(2) : 
                            (lesson.duration / 60).toFixed(2)
                          } hrs
                        </span>
                      )}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Mode:</span>
                      {editingLesson === lesson._id ? (
                        <select
                          className="edit-input"
                          value={editedData.mode || ''}
                          onChange={(e) => setEditedData({ ...editedData, mode: e.target.value })}
                        >
                          <option value="">Select mode</option>
                          <option value="Online">Online</option>
                          <option value="Physical">Physical</option>
                        </select>
                      ) : (
                        <span className="detail-value">
                          {savedEdits[lesson._id]?.mode || lesson.mode || 'N/A'}
                        </span>
                      )}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Tutor Rate:</span>
                      {editingLesson === lesson._id ? (
                        <input
                          type="number"
                          className="edit-input"
                          value={editedData.tutorRate}
                          onChange={(e) => setEditedData({ ...editedData, tutorRate: parseFloat(e.target.value) })}
                          min="0"
                          step="0.01"
                        />
                      ) : (
                        <span className="detail-value">
                          ${(savedEdits[lesson._id]?.tutorRate !== undefined ? 
                            savedEdits[lesson._id].tutorRate : 
                            lesson.tutorRate
                          )?.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="detail-row detail-row-full">
                      <span className="detail-label">Summary:</span>
                      {editingLesson === lesson._id ? (
                        <textarea
                          className="edit-textarea"
                          value={editedData.summary}
                          onChange={(e) => setEditedData({ ...editedData, summary: e.target.value })}
                          rows="3"
                        />
                      ) : (
                        <span className="detail-value">
                          {savedEdits[lesson._id]?.summary !== undefined ? 
                            savedEdits[lesson._id].summary : 
                            (lesson.summary || 'No summary')
                          }
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="dispute-reason">
                    <strong>Dispute Reason:</strong>
                    <p>{lesson.disputeReason}</p>
                  </div>

                  <div className="dispute-actions">
                    {editingLesson === lesson._id ? (
                      <>
                        <button 
                          className="btn-save"
                          onClick={() => handleSaveEdit(lesson._id)}
                          disabled={submitting}
                        >
                          Save
                        </button>
                        <button 
                          className="btn-cancel"
                          onClick={() => {
                            setEditingLesson(null);
                            setEditedData({});
                          }}
                          disabled={submitting}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDeleteLesson(lesson._id)}
                          disabled={submitting}
                          title="Permanently delete this lesson"
                        >
                          Delete Lesson
                        </button>
                        <button 
                          className="btn-edit"
                          onClick={() => {
                            setEditingLesson(lesson._id);
                            // Extract the date (YYYY-MM-DD) and time (HH:MM 24h)
                            // from the stored UTC date pinned to SGT so the
                            // inputs show the Singapore date/time, not the
                            // browser's local interpretation.
                            setEditedData({
                              date: lesson.date
                                ? new Date(lesson.date).toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' })
                                : todaySGT(),
                              time: lesson.date
                                ? new Date(lesson.date).toLocaleTimeString('en-GB', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: false })
                                : '',
                              duration: lesson.duration / 60, // Convert minutes to hours
                              mode: lesson.mode || '',
                              tutorRate: lesson.tutorRate,
                              summary: lesson.summary || ''
                            });
                          }}
                          disabled={submitting}
                        >
                          Edit Lesson
                        </button>
                        <button 
                          className="btn-resolve"
                          onClick={() => {
                            setResolutionModal({ isOpen: true, lesson });
                            setResolutionNotes('');
                          }}
                          disabled={submitting}
                        >
                          Resolve Dispute
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn-cancel-modal"
            onClick={onClose}
            disabled={submitting}
          >
            Close
          </button>
        </div>
      </div>

      {/* Resolution Modal */}
      {resolutionModal.isOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setResolutionModal({ isOpen: false, lesson: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Resolve Dispute</h3>
              <button 
                className="modal-close"
                onClick={() => setResolutionModal({ isOpen: false, lesson: null })}
                disabled={submitting}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="lesson-summary">
                <h4>Lesson Details</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="label">Tutee:</span>
                    <span className="value">{resolutionModal.lesson?.tutee?.name || 'Unknown'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Subject:</span>
                    <span className="value">{resolutionModal.lesson?.subject}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Date:</span>
                    <span className="value">{formatDate(resolutionModal.lesson?.date)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Time:</span>
                    <span className="value">
                      {resolutionModal.lesson?.date ? new Date(resolutionModal.lesson.date).toLocaleTimeString('en-SG', { 
                        timeZone: 'Asia/Singapore',
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      }) : 'N/A'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Duration:</span>
                    <span className="value">{(resolutionModal.lesson?.duration / 60).toFixed(2)} hrs</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Mode:</span>
                    <span className="value">{resolutionModal.lesson?.mode || 'N/A'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Tutor Rate:</span>
                    <span className="value">${resolutionModal.lesson?.tutorRate?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="dispute-info">
                <h4>Dispute Reason</h4>
                <p className="dispute-reason-text">{resolutionModal.lesson?.disputeReason}</p>
              </div>

              {savedEdits[resolutionModal.lesson?._id] && (() => {
                const edits = savedEdits[resolutionModal.lesson._id];
                const originalDate = new Date(resolutionModal.lesson?.date);
                // Extract the original date/time in SGT so they match the
                // SGT-based edits.date/edits.time and avoid false "changed" diffs.
                const originalDateStr = originalDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' });
                const originalTimeStr = originalDate.toLocaleTimeString('en-GB', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: false });
                
                const hasChanges = 
                  (edits.date && edits.date !== originalDateStr) ||
                  (edits.time && edits.time !== originalTimeStr) ||
                  (edits.duration && edits.duration !== resolutionModal.lesson?.duration) ||
                  (edits.mode && edits.mode !== resolutionModal.lesson?.mode) ||
                  (edits.tutorRate && edits.tutorRate !== resolutionModal.lesson?.tutorRate) ||
                  (edits.summary && edits.summary !== (resolutionModal.lesson?.summary || ''));
                
                if (!hasChanges) return null;
                
                return (
                  <div className="edited-data-info">
                    <h4>⚠️ Edited Lesson Data</h4>
                    <p className="info-text">The following changes will be applied when resolving this dispute:</p>
                    <ul>
                      {edits.date && edits.date !== originalDateStr && (
                        <li>Date: {formatDate(resolutionModal.lesson?.date)} → {formatDate(edits.date)}</li>
                      )}
                      {edits.time && edits.time !== originalTimeStr && (
                        <li>Time: {originalDate.toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: true })} → {edits.time}</li>
                      )}
                      {edits.duration && edits.duration !== resolutionModal.lesson?.duration && (
                        <li>Duration: {(resolutionModal.lesson?.duration / 60).toFixed(2)}h → {(edits.duration / 60).toFixed(2)}h</li>
                      )}
                      {edits.mode && edits.mode !== resolutionModal.lesson?.mode && (
                        <li>Mode: {resolutionModal.lesson?.mode || 'N/A'} → {edits.mode}</li>
                      )}
                      {edits.tutorRate && edits.tutorRate !== resolutionModal.lesson?.tutorRate && (
                        <li>Tutor Rate: ${resolutionModal.lesson?.tutorRate?.toFixed(2)} → ${edits.tutorRate?.toFixed(2)}</li>
                      )}
                      {edits.summary && edits.summary !== (resolutionModal.lesson?.summary || '') && (
                        <li>Summary updated</li>
                      )}
                    </ul>
                  </div>
                );
              })()}

              <div className="resolution-notes">
                <label htmlFor="resolutionNotes">
                  Resolution Notes <span className="optional">(optional)</span>
                </label>
                <p className="help-text">You can resolve this dispute without making any changes to the lesson. Simply add resolution notes (optional) and click "Resolve Dispute".</p>
                <textarea
                  id="resolutionNotes"
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Add notes about how this dispute was resolved..."
                  rows="4"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-cancel-modal"
                onClick={() => setResolutionModal({ isOpen: false, lesson: null })}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-resolve-modal"
                onClick={handleResolveDispute}
                disabled={submitting}
              >
                {submitting ? 'Resolving...' : 'Resolve Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeResolutionModal;